/**
 * Live camera capture for the badge step (CONFERENCE-INTAKE).
 *
 * `<input capture>` only opens a camera on phones and tablets — a laptop's
 * browser ignores it and shows the file picker. This uses the camera directly
 * (getUserMedia): a live preview, the QR decoded from the video as you aim
 * (so you know it's readable before you capture), and a Capture button that
 * hands back a still.
 *
 * Browsers only allow the camera on secure pages (https, or localhost); the
 * caller checks `liveCameraAvailable()` and falls back to the file input.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import jsQR from 'jsqr';

/** Whether this page may use the camera directly. */
export function liveCameraAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext === true &&
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getUserMedia === 'function'
  );
}

/** How often the preview is checked for a QR code. */
const QR_SCAN_MS = 400;
/** Width the preview frame is scaled to for the QR scan (speed). */
const QR_SCAN_WIDTH = 640;

function cameraError(err: unknown): string {
  const name = (err as { name?: string })?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Camera access was blocked. Allow the camera for this site in the browser, or choose a photo instead.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'No camera was found. Choose a photo instead.';
  }
  if (name === 'NotReadableError') {
    return 'The camera is in use by another app. Close it, or choose a photo instead.';
  }
  return "Couldn't start the camera. Choose a photo instead.";
}

export function CameraCapture({
  open,
  onClose,
  onCapture,
  onUseFile,
}: {
  open: boolean;
  onClose: () => void;
  /** The captured still (JPEG) and the QR seen in the preview, if any. */
  onCapture: (photo: Blob, qr: string | null) => void;
  /** The camera failed or was refused — let the caller offer the file picker. */
  onUseFile: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [deviceId, setDeviceId] = useState<string>('');

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setReady(false);
  }, []);

  // Start (or switch) the camera while the dialog is open.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setQr(null);
    const constraints: MediaStreamConstraints = {
      audio: false,
      video: deviceId
        ? { deviceId: { exact: deviceId }, width: { ideal: 1920 }, height: { ideal: 1080 } }
        : { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
    };
    navigator.mediaDevices
      .getUserMedia(constraints)
      .then(async (stream) => {
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play().catch(() => undefined);
        }
        setReady(true);
        // Labels are only available once permission is granted.
        const all = await navigator.mediaDevices.enumerateDevices().catch(() => []);
        if (!cancelled) setCameras(all.filter((d) => d.kind === 'videoinput'));
      })
      .catch((err) => {
        if (!cancelled) setError(cameraError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [open, deviceId]);

  // Release the camera when the dialog closes or unmounts.
  useEffect(() => {
    if (!open) stop();
    return stop;
  }, [open, stop]);

  // Look for the QR in the live preview while aiming.
  useEffect(() => {
    if (!open || !ready) return;
    const canvas = document.createElement('canvas');
    const id = window.setInterval(() => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return;
      const scale = Math.min(1, QR_SCAN_WIDTH / video.videoWidth);
      canvas.width = Math.round(video.videoWidth * scale);
      canvas.height = Math.round(video.videoHeight * scale);
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const { data, width, height } = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const hit = jsQR(data, width, height, { inversionAttempts: 'dontInvert' });
      const text = hit?.data?.trim();
      if (text) setQr(text);
    }, QR_SCAN_MS);
    return () => window.clearInterval(id);
  }, [open, ready]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        stop();
        onCapture(blob, qr);
      },
      'image/jpeg',
      0.92,
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Photograph the badge</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={onUseFile}>
                Choose a photo
              </Button>
            }
          >
            {error}
          </Alert>
        ) : (
          <>
            <Box
              sx={{
                position: 'relative',
                bgcolor: 'common.black',
                borderRadius: 1,
                overflow: 'hidden',
                aspectRatio: '16 / 9',
              }}
            >
              <Box
                component="video"
                ref={videoRef}
                muted
                playsInline
                aria-label="Camera preview"
                sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
              />
              {qr && (
                <Chip
                  color="success"
                  label={`QR found: ${qr}`}
                  sx={{ position: 'absolute', top: 12, left: 12 }}
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Fill the frame with the badge, hold it still, and capture when the
              name is sharp{qr ? '' : ' — the QR code is recognised automatically when it is in view'}.
            </Typography>
            {cameras.length > 1 && (
              <TextField
                select
                size="small"
                label="Camera"
                value={deviceId || streamRef.current?.getVideoTracks()[0]?.getSettings().deviceId || ''}
                onChange={(e) => setDeviceId(e.target.value)}
                sx={{ mt: 2, minWidth: 260 }}
              >
                {cameras.map((c, i) => (
                  <MenuItem key={c.deviceId} value={c.deviceId}>
                    {c.label || `Camera ${i + 1}`}
                  </MenuItem>
                ))}
              </TextField>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onUseFile}>Choose a photo instead</Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={<PhotoCameraIcon />}
          onClick={capture}
          disabled={!ready || error !== null}
        >
          Capture
        </Button>
      </DialogActions>
    </Dialog>
  );
}
